import { type EnergyEfficiencyFlagProps as UiEnergyEfficiencyFlagProps } from '@packages/shared/src/ui/EnergyEfficiencyFlag/EnergyEfficiencyFlag';

import { isValidColor } from '@packages/shared/src/ui/EnergyEfficiencyFlag/colorsForEnergyEfficiency';
import type { EnergieEfficiency, EnergieEfficiencyOld } from '../../types';
import { EnergyEfficiencyFlagLocalized } from '../EnergyEfficiencyFlagLocalized/EnergyEfficiencyFlagLocalized.client';
import { isEfficiencyClass } from '../../helper/isEfficiencyClass';

/**
 * Props for rendering either the legacy or the new energy efficiency flag.
 *
 * Accepts both old and new API shapes and forwards `size` to the shared UI flag.
 */
export type EnergyEfficiencyFlagProps = (EnergieEfficiency | EnergieEfficiencyOld) &
  Pick<UiEnergyEfficiencyFlagProps, 'size'>;

/**
 * Renders a localized energy efficiency flag.
 *
 * Legacy flags are rendered when `hasOldLabel` is true and a valid legacy color is available.
 * Otherwise, a valid new efficiency class is rendered.
 * Invalid data returns `null`.
 */
export const EnergyEfficiencyFlag = ({ size, ...props }: EnergyEfficiencyFlagProps) => {
  if (props.hasOldLabel) {
    const { color, level } = props;
    const validatedColor = isValidColor(color) ? color : undefined;

    if (validatedColor) {
      return (
        <EnergyEfficiencyFlagLocalized
          variant="legacy"
          text={level}
          color={validatedColor}
          size={size}
        />
      );
    }
  }

  const validatedLevel = isEfficiencyClass(props.level) ? props.level : undefined;

  if (validatedLevel) {
    return (
      <EnergyEfficiencyFlagLocalized variant="new" efficiencyClass={validatedLevel} size={size} />
    );
  }

  return null;
};
